import React, { useState, useEffect, useCallback, useRef } from 'react';
import { completeTourApi } from '../api';
import { useTranslation } from '../i18n/LanguageContext';
import {
  Gauge, MapTrifold, Cube, Cloud, Factory, Scales, Notebook,
  Rocket, ArrowRight, ArrowLeft, CheckCircle, Sparkle
} from '@phosphor-icons/react';

const TOUR_STEP_DEFS = [
  { id: 'welcome', target: null, titleKey: 'tour.welcomeTitle', subtitleKey: 'tour.welcomeSubtitle', bodyKey: 'tour.welcomeBody', icon: Rocket, position: 'center' },
  { id: 'cockpit', target: '[data-testid="nav-cockpit"]', titleKey: 'tour.cockpitTitle', bodyKey: 'tour.cockpitBody', icon: Gauge, position: 'right' },
  { id: 'heatmap', target: '[data-testid="nav-heatmap"]', titleKey: 'tour.heatmapTitle', bodyKey: 'tour.heatmapBody', icon: MapTrifold, position: 'right' },
  { id: 'twin', target: '[data-testid="nav-twin"]', titleKey: 'tour.twinTitle', bodyKey: 'tour.twinBody', icon: Cube, position: 'right' },
  { id: 'emissions', target: '[data-testid="nav-emissions"]', titleKey: 'tour.emissionsTitle', bodyKey: 'tour.emissionsBody', icon: Cloud, position: 'right' },
  { id: 'oee', target: '[data-testid="nav-oee"]', titleKey: 'tour.oeeTitle', bodyKey: 'tour.oeeBody', icon: Factory, position: 'right' },
  { id: 'cbam', target: '[data-testid="nav-cbam"]', titleKey: 'tour.cbamTitle', bodyKey: 'tour.cbamBody', icon: Scales, position: 'right' },
  { id: 'reports', target: '[data-testid="nav-reports"]', titleKey: 'tour.reportsTitle', bodyKey: 'tour.reportsBody', icon: Notebook, position: 'right' },
  { id: 'industry', target: '[data-testid="industry-selector"]', titleKey: 'tour.industryTitle', bodyKey: 'tour.industryBody', icon: Sparkle, position: 'bottom' },
  { id: 'complete', target: null, titleKey: 'tour.completeTitle', subtitleKey: 'tour.completeSubtitle', bodyKey: 'tour.completeBody', icon: CheckCircle, position: 'center' },
];

function Spotlight({ rect }) {
  if (!rect) return null;
  const pad = 6;
  const r = 10;
  return (
    <svg className="fixed inset-0 z-[9998] pointer-events-none" width="100%" height="100%" style={{ position: 'fixed', top: 0, left: 0 }}>
      <defs>
        <mask id="tour-mask">
          <rect x="0" y="0" width="100%" height="100%" fill="white" />
          <rect x={rect.left - pad} y={rect.top - pad} width={rect.width + pad * 2} height={rect.height + pad * 2} rx={r} ry={r} fill="black" />
        </mask>
      </defs>
      <rect x="0" y="0" width="100%" height="100%" fill="rgba(10, 12, 8, 0.75)" mask="url(#tour-mask)" />
      <rect x={rect.left - pad} y={rect.top - pad} width={rect.width + pad * 2} height={rect.height + pad * 2} rx={r} ry={r} fill="none" stroke="rgba(163, 163, 128, 0.5)" strokeWidth="2" />
    </svg>
  );
}

function TooltipCard({ step, stepIndex, totalSteps, onNext, onPrev, onSkip, rect, t }) {
  const cardRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const Icon = step.icon;

  useEffect(() => {
    if (!cardRef.current) return;
    const card = cardRef.current.getBoundingClientRect();
    if (step.position === 'center' || !rect) {
      setPos({ top: Math.max(20, (window.innerHeight - card.height) / 2), left: Math.max(20, (window.innerWidth - card.width) / 2) });
      return;
    }
    let top, left;
    if (step.position === 'right') {
      top = rect.top + rect.height / 2 - card.height / 2;
      left = rect.right + 20;
      if (left + card.width > window.innerWidth - 20) left = rect.left - card.width - 20;
    } else if (step.position === 'bottom') {
      top = rect.bottom + 16;
      left = rect.left + rect.width / 2 - card.width / 2;
    }
    top = Math.max(16, Math.min(top, window.innerHeight - card.height - 16));
    left = Math.max(16, Math.min(left, window.innerWidth - card.width - 16));
    setPos({ top, left });
  }, [rect, step.position]);

  const isFirst = stepIndex === 0;
  const isLast = stepIndex === totalSteps - 1;
  const isCenterStep = step.position === 'center';

  return (
    <div ref={cardRef} className="fixed z-[9999] animate-fade-in" style={{ top: pos.top, left: pos.left, maxWidth: isCenterStep ? 440 : 360 }} data-testid="tour-card">
      <div className={`bg-[#1A2015] border border-[#A3A380]/20 rounded-2xl shadow-2xl overflow-hidden ${isCenterStep ? 'text-center' : ''}`}>
        <div className="h-1 bg-[#2A3020]">
          <div className="h-full bg-gradient-to-r from-[#4A5D23] to-[#A3A380] transition-all duration-500 ease-out" style={{ width: `${((stepIndex + 1) / totalSteps) * 100}%` }} />
        </div>
        <div className={`p-6 ${isCenterStep ? 'px-8 py-8' : ''}`}>
          <div className={`${isCenterStep ? 'mx-auto' : ''} w-12 h-12 rounded-xl bg-[#4A5D23]/30 flex items-center justify-center mb-4`}>
            <Icon size={24} weight="duotone" className="text-[#A3A380]" />
          </div>
          {step.subtitleKey && <p className="text-[10px] font-body font-semibold uppercase tracking-[0.2em] text-[#A3A380] mb-1">{t(step.subtitleKey)}</p>}
          <h3 className="font-heading text-lg font-bold text-white mb-2">{t(step.titleKey)}</h3>
          <p className="font-body text-sm text-[#A3A380]/80 leading-relaxed mb-6">{t(step.bodyKey)}</p>
          <div className={`flex items-center ${isCenterStep ? 'justify-center' : 'justify-between'} gap-3`}>
            {!isCenterStep && <button onClick={onSkip} className="text-xs font-body text-[#A3A380]/50 hover:text-[#A3A380] transition-colors" data-testid="tour-skip">{t('tour.skipTour')}</button>}
            <div className="flex items-center gap-2">
              {!isFirst && !isCenterStep && (
                <button onClick={onPrev} className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-body font-medium text-[#A3A380] hover:bg-white/5 transition-colors" data-testid="tour-prev">
                  <ArrowLeft size={14} />{t('tour.back')}
                </button>
              )}
              {isFirst && (
                <>
                  <button onClick={onSkip} className="px-4 py-2.5 rounded-lg text-xs font-body font-medium text-[#A3A380]/60 hover:bg-white/5 transition-colors" data-testid="tour-skip">{t('tour.skip')}</button>
                  <button onClick={onNext} className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#4A5D23] hover:bg-[#5A6D33] text-white text-sm font-body font-semibold transition-all" data-testid="tour-start">
                    {t('tour.startTour')}<ArrowRight size={16} />
                  </button>
                </>
              )}
              {!isFirst && !isLast && (
                <button onClick={onNext} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#4A5D23] hover:bg-[#5A6D33] text-white text-xs font-body font-semibold transition-all" data-testid="tour-next">
                  {t('tour.next')}<ArrowRight size={14} />
                </button>
              )}
              {isLast && (
                <button onClick={onSkip} className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#4A5D23] hover:bg-[#5A6D33] text-white text-sm font-body font-semibold transition-all" data-testid="tour-finish">
                  <CheckCircle size={18} weight="fill" />{t('tour.getStarted')}
                </button>
              )}
            </div>
          </div>
          {!isCenterStep && (
            <div className="flex justify-center gap-1.5 mt-4">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div key={i} className={`h-1 rounded-full transition-all duration-300 ${i === stepIndex ? 'w-5 bg-[#A3A380]' : i < stepIndex ? 'w-2 bg-[#4A5D23]' : 'w-2 bg-[#2A3020]'}`} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function GuidedTour({ isOpen, onClose, userRole }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);

  const roleAccess = {
    Executive: ['cockpit', 'heatmap', 'twin', 'emissions', 'oee', 'cbam', 'reports', 'industry'],
    Operations: ['cockpit', 'heatmap', 'twin', 'emissions', 'oee', 'cbam', 'reports', 'industry'],
    Sustainability: ['cockpit', 'heatmap', 'emissions', 'oee', 'cbam', 'reports', 'industry'],
    Finance: ['cockpit', 'heatmap', 'industry'],
  };

  const allowed = roleAccess[userRole] || roleAccess['Executive'];
  const steps = TOUR_STEP_DEFS.filter(s => s.position === 'center' || allowed.includes(s.id));
  const currentStep = steps[step];

  const updateTargetRect = useCallback(() => {
    if (!currentStep?.target) { setTargetRect(null); return; }
    const el = document.querySelector(currentStep.target);
    if (el) {
      const r = el.getBoundingClientRect();
      setTargetRect({ top: r.top, left: r.left, width: r.width, height: r.height, right: r.right, bottom: r.bottom });
    } else { setTargetRect(null); }
  }, [currentStep]);

  useEffect(() => {
    if (!isOpen) return;
    updateTargetRect();
    window.addEventListener('resize', updateTargetRect);
    return () => window.removeEventListener('resize', updateTargetRect);
  }, [isOpen, step, updateTargetRect]);

  useEffect(() => { if (!isOpen) setStep(0); }, [isOpen]);

  const handleNext = () => { if (step < steps.length - 1) setStep(step + 1); };
  const handlePrev = () => { if (step > 0) setStep(step - 1); };
  const handleClose = async () => {
    try { await completeTourApi(); } catch { /* ok */ }
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  if (!isOpen || !currentStep) return null;

  return (
    <>
      {currentStep.position === 'center' && <div className="fixed inset-0 z-[9997] bg-[#0A0C08]/75" onClick={(e) => e.stopPropagation()} />}
      {currentStep.target && <Spotlight rect={targetRect} />}
      {currentStep.target && <div className="fixed inset-0 z-[9998]" style={{ pointerEvents: 'auto', background: 'transparent' }} />}
      <TooltipCard step={currentStep} stepIndex={step} totalSteps={steps.length} onNext={handleNext} onPrev={handlePrev} onSkip={handleClose} rect={targetRect} t={t} />
    </>
  );
}
